/**
 * Weapon archetypes: what a turret *does*, as opposed to what its numbers are.
 *
 * Two things live here and they are deliberately in the same file.
 *
 * 1. `WeaponProfile` — the mechanical shape of a gun. A turret is not just a
 *    damage number and a reload; it is "four barrels at once", "three rounds
 *    then a long pause", "hold the trigger and let go". Those behaviours cannot
 *    be expressed as columns on a catalogue row, so they are declared here as
 *    data and joined onto the row by id in `stat-resolution.ts`.
 *
 * 2. `stepTrigger` — the state machine that turns "the fire flag was held on
 *    this tick" into "these shells leave the muzzle". It is pure and takes no
 *    delta time, exactly like `stepTank`, and it runs in **two** places: the
 *    arena, authoritatively, and the browser, to predict its own tracers. Both
 *    must produce identical volleys from identical inputs.
 *
 * Browser-safe by construction: no `@nestjs/*`, no Prisma, no Node built-ins,
 * no `Date.now()`, no `Math.random()`.
 *
 * ── Why the catalogue does not hold any of this ───────────────────────────────
 * `CatalogItem` is a Postgres table with one column per stat. Adding `pierce`,
 * `chargeTicks` and a dozen siblings would mean a migration for every new
 * mechanic, and would push behaviour into a place where a stray UPDATE could
 * change it. Mechanics are code-shaped, so they are versioned with the code and
 * the database stays a projection of the numbers only.
 */
import { TICK_DT, TICK_RATE } from './protocol';
import type { HullId, TankSpec, TurretId } from './tank-spec';

// ─── 3D models ───────────────────────────────────────────────────────────────
//
// Every hull and turret carries a model reference. **Thirty-seven of the forty
// now point at their own authored mesh**; only `hull_recruit`, `turret_recruit`
// and `turret_breaker` still wear the defaults — the first two by design, the
// third because no delivered model matched its concept art.
//
// The indirection is what made that cheap, and it is why it stays: fitting a
// newly authored mesh is one string in the table below plus its yaw, with no
// code path, no loader change and no conditional. Ten hulls and ten turrets
// arrived that way in one drop without a line of the renderer moving.
//
// The client resolves these through `TankSpec.hullModel` / `TankSpec.turretModel`
// (its own tank) or through `modelForHull` / `modelForTurret` keyed by the ids in
// the `loadout` battle event (everyone else's).

/** The fallback hull mesh, worn by the recruit chassis and anything unknown. */
export const DEFAULT_HULL_MODEL = '/assets/tank-hull.glb';
/** The fallback turret mesh. */
export const DEFAULT_TURRET_MODEL = '/assets/tank-turret.glb';

/**
 * Yaw corrections for the two fallback meshes.
 *
 * The default hull was authored nose-down +Z and needs none; the default
 * turret's barrel runs along its -X, hence the quarter turn. These are the
 * values `tank.ts` used to carry as module constants, before per-item art meant
 * every model needed its own.
 */
export const DEFAULT_HULL_YAW = 0;
export const DEFAULT_TURRET_YAW = Math.PI / 2;

// ─── Archetypes ──────────────────────────────────────────────────────────────

/**
 * What kind of gun this is. Purely descriptive — nothing branches on it; the
 * behaviour comes from the numeric fields below, so a new archetype is a new
 * combination rather than a new `switch` arm. It exists so the garage can name
 * the thing and so tests can assert coverage of the roster.
 */
export type WeaponArchetype =
  | 'single'
  | 'multi'
  | 'burst'
  | 'charge'
  | 'scatter'
  | 'mortar'
  | 'ricochet'
  | 'rail';

/**
 * The mechanical shape of one gun.
 *
 * Every field is inert at its zero value, so `SINGLE_SHOT` below is the whole
 * pre-existing behaviour written out, and a new turret only states what makes
 * it different.
 */
export interface WeaponProfile {
  archetype: WeaponArchetype;

  /** Shells per trigger pull. */
  shellsPerVolley: number;
  /**
   * Radians between adjacent shells in a volley — a fixed fan, not randomness.
   *
   * The fan is why a quad barrel is devastating at 20 m and mediocre at 80: the
   * outer shells diverge with distance, so range thins the volley on its own
   * without a damage-falloff rule bolted on top.
   */
  volleySpread: number;
  /**
   * Metres each shell is offset sideways from the muzzle, spread across the
   * volley. Models parallel barrels: the shells stay parallel, so a twin gun
   * lands both rounds at any range and simply looks wide.
   */
  volleyOffset: number;
  /**
   * Ticks between rounds *inside* a burst. 0 fires the whole volley at once.
   *
   * A committed burst runs to completion whether or not the trigger is still
   * held — releasing early cannot cancel it, which is what makes the long
   * reload afterwards a real cost rather than something to tap out of.
   */
  burstGapTicks: number;

  /**
   * Ticks of held trigger to reach full charge. 0 means not a charge weapon.
   *
   * The charge is measured by **how long the server saw `fire` held**, never by
   * anything the client reports. There is no charge level in the protocol for a
   * modified client to forge, for the same reason there is no aim angle.
   */
  chargeTicks: number;
  /** Damage and muzzle-velocity multiplier for a shot released at zero charge. */
  minChargeScale: number;
  /** Multiplier at full charge. */
  maxChargeScale: number;

  /**
   * Half-angle of the random cone, in radians. 0 is a perfectly true barrel.
   *
   * The randomness is derived, not drawn: see `shellNoise`. Server and client
   * compute the identical cone from the identical shot ordinal.
   */
  scatterCone: number;

  /** Extra targets a shell passes through before it stops. */
  pierce: number;
  /** Arena-wall reflections before the shell gives up. */
  bounces: number;
  /**
   * Metres the shell must travel before it can hit anything.
   *
   * This is how a mortar's arc is expressed in a flat simulation: near the
   * shooter the round is above head height, so it flies over the near field and
   * only becomes dangerous past its arming distance. A minimum range falls out
   * of it for free.
   */
  armDistance: number;
  /** Area-damage radius on detonation, in metres. 0 is a single-target shell. */
  blastRadius: number;

  /**
   * Metres the impact arcs on to a *second* target. 0 is a shell that stops
   * where it lands.
   *
   * Deliberately not a `blastRadius` and deliberately not an archetype of its
   * own — see `turret_arclight` below for both arguments. It picks exactly one
   * tank, the nearest other one the shell was allowed to hit, and it does not
   * chain onward from there.
   */
  chainRadius: number;
  /**
   * What the arced hit carries, as a fraction of the shell's damage.
   *
   * Applied before armour, exactly like a blast's falloff, so the second tank's
   * plate protects it the same way the first tank's did.
   */
  chainDamageScale: number;

  /** Peak height of the rendered arc, in metres. Cosmetic; never simulated. */
  arcHeight: number;
}

/** The plain gun: one shell, one reload. Every profile starts from this. */
export const SINGLE_SHOT: WeaponProfile = {
  archetype: 'single',
  shellsPerVolley: 1,
  volleySpread: 0,
  volleyOffset: 0,
  burstGapTicks: 0,
  chargeTicks: 0,
  minChargeScale: 1,
  maxChargeScale: 1,
  scatterCone: 0,
  pierce: 0,
  bounces: 0,
  armDistance: 0,
  blastRadius: 0,
  chainRadius: 0,
  chainDamageScale: 0,
  arcHeight: 0,
};

function weapon(over: Partial<WeaponProfile>): WeaponProfile {
  return { ...SINGLE_SHOT, ...over };
}

// ─── The roster ──────────────────────────────────────────────────────────────

/** A catalogue entry's non-numeric half: how it behaves and what it looks like. */
export interface TurretProfile {
  /**
   * How deep in the progression this gun is — 1 for the yard hack, 5 for the
   * premium capstones. A **copy** of `CatalogueSeedItem.tier`, and see
   * `garage/catalogue-parity.test.ts` for what keeps it honest.
   *
   * It lives here for exactly the reason `withdrawn` does, one line further
   * down: the catalogue is the garage's, the tier has to be honoured by the
   * battle too, and the battle has no account to resolve a bot's spec through.
   * This file is the one place both sides already agree on what a turret *is*,
   * so it is where "how far up the ladder is this one" belongs.
   *
   * What reads it is the tier cap — the ceiling on what an AI tank may carry,
   * set by a mission's difficulty and by the rank of the lowest-ranked human in
   * the arena (`dealableLoadout` in `battle/bots.ts`). Nothing in the
   * simulation reads it, and nothing may: a tier is a fact about a shop shelf,
   * not about a shell.
   */
  tier: number;
  /** Path to the turret mesh. */
  model: string;
  /**
   * Yaw, in radians, that brings the barrel round onto the hull's +Z.
   *
   * Image-to-3D gives every model its own idea of which way is forward, so this
   * is per mesh: some run the barrel along -X and need a quarter turn one way,
   * some the other, and a few were authored down +Z and need none. Get it wrong
   * by 90° and the tank shoots out of its own flank while the shell travels
   * forwards; get it wrong by 180° and it fires out of its own back.
   *
   * **Seven of the twelve were 180° out**, and had been since the art landed:
   * Hornet, Buckshot, Stutter, Howler, Carom, Tempest and Railspike all carried
   * `+π/2` where they needed `-π/2`. Nothing caught it — the turret still seats
   * perfectly, the muzzle flash still leaves the pivot's +Z, and the arena's
   * camera sits behind the *gun's bearing* rather than behind the mesh, so the
   * sight and the shell agreed with each other while the model disagreed with
   * both. It took photographing all 96 pairs in a live arena to see, because
   * that is the one view where a correct barrel must point away from the
   * viewer.
   *
   * The fix belongs here rather than in the meshes. Rotating seven GLBs would
   * put seven new binaries in history where the change is invisible in a diff,
   * would have to go back through the weld → simplify → optimize pipeline, and
   * would be lost the moment anybody re-exported one. This field exists for
   * exactly this and costs one number.
   *
   * `yarn e2e hull-turret-matrix` now fails a pair whose barrel points
   * backwards, measured rather than eyeballed — a gun tube is the geometry that
   * is thin in *both* cross-axes, so which side of the turret body it extends
   * from is a number.
   */
  yaw: number;
  /**
   * Metres of this mesh that belong **inside** the hull, measured from its base.
   *
   * Every turret GLB arrives normalised with its base at y = 0, but they do not
   * agree on what is at that base. Some are a bare turret body; some stand on a
   * collar or a plinth that the artist drew as part of the model. So the mesh
   * base is *not* the line that should meet the deck — the underside of the
   * turret **body** is, and this is the distance between the two.
   *
   * `tank.ts` sinks the model by exactly this much, which puts the body's
   * underside on the deck and the collar inside the hull where it belongs.
   * Zero for a bare body, and the number is the mesh's business rather than the
   * hull's, which is why it lives here beside `model` and `yaw` — all three are
   * facts about one exported file and all three move together.
   *
   * Measured, never guessed: `frontend/e2e/rig/model-rig.ts` walks down from the
   * turret's widest horizontal slice and stops where the profile stops being
   * body-width. `yarn e2e hull-turret-matrix` reports it as *Stand*.
   */
  mountDrop: number;
  /**
   * Metres the turret's mesh is pushed **forward inside its pivot**, at the
   * unscaled size, so its body sits on the ring rather than behind it.
   *
   * **This corrects the mesh's own origin, and that is why it moves the mesh and
   * not the pivot.** Image-to-3D centres a model on its bounding box; a turret's
   * longest axis is almost always the barrel, so the origin lands out in the gun
   * tube well forward of the ring. Once `yaw` brings the barrel round onto +Z the
   * body is left that same distance *behind* the mounting point — measured across
   * this roster at 0.05 m to 1.20 m of it, tracking barrel length almost linearly.
   * A player sees the turret parked at the back of the deck with the hull's own
   * ring plate visible in front of it, which is exactly how it was reported.
   *
   * Read the trap in `turret-fit.ts` before assuming this is the same thing as
   * `offsetZ`, because the two look identical and are opposites. `offsetZ` is a
   * *mounting position* — a fact about the hull — and has to move the pivot, above
   * the rotation, or the turret orbits its own mount as it traverses. This is a
   * fact about one authored file, and the turret must traverse about its own ring,
   * so it belongs inside the pivot where the rotation carries it.
   *
   * Scaled by `sizeScale` for the same reason `mountDrop` is: it is measured on
   * the unscaled mesh, so a resize has to carry it. **`muzzleReach` moves with
   * it** — the visible barrel tip travels the same distance — and the three
   * turrets with the smallest values here (Buckshot 0.07, Mauler 0.34, Bellows
   * 0.36) are exactly the three the fit matrix cannot judge a barrel on, which is
   * the mechanism confirming itself: no long thin tube, no displaced box.
   *
   * Measured as `-turretBodyCentreZ / sizeScale` by `model-rig.ts`, off the same
   * thickness profile the barrel verdict is taken from.
   */
  mountForward: number;
  /**
   * Multiplier on `TURRET_SCALE`, sizing this turret against the hull.
   *
   * One uniform scale could not do this, and the reason is the same one that
   * broke turret *seating*: image-to-3D normalises whichever axis is longest,
   * and on all twelve of these that axis is the **barrel**. So a single scale
   * makes every gun the same length and lets the bodies land wherever their
   * artist's proportions put them — body height above deck ran 1.06 m on the
   * Lancer to 2.66 m on the Hailstorm, against hulls 1.57 m to 2.18 m tall. A
   * Hailstorm out-topped the Juggernaut carrying it; a Lancer was a cupola on a
   * Paladin.
   *
   * Set so the turret fits an envelope — 2.30 m across the ring, 1.80 m of body
   * above the deck — taking whichever of the two binds first. Fitting *both*
   * rather than matching one is what keeps the roster varied instead of merely
   * moving the outlier: matching ring width alone would have inflated the
   * Tempest to 3.65 m tall.
   *
   * `mountDrop` is in metres at the **unscaled** size, so `tank.ts` multiplies
   * the two. Keeping it that way means a re-measure of the collar does not have
   * to be redone every time the size is tuned.
   */
  sizeScale: number;
  /**
   * Metres from the turret pivot to this gun's barrel tip, after `sizeScale`.
   *
   * Where the muzzle flash is drawn. It has to be per turret for the same reason
   * the size does — once guns are different lengths, one offset is right for one
   * of them. `MUZZLE_OFFSET` in `protocol.ts` is deliberately **not** this: that
   * is where the simulation spawns the shell, it stays a single shared constant,
   * and the two now differ by up to 0.65 m on the shortest gun. See
   * `TankVisual.muzzleWorld`.
   */
  muzzleReach: number;
  weapon: WeaponProfile;
  /**
   * Set to take this turret out of the game without deleting anything.
   *
   * A withdrawn turret disappears from the shop, cannot be bought or equipped,
   * is not dealt to a bot, and stops resolving for a player who already had it
   * fitted — they drive the stock turret instead, and are moved back onto it by
   * the loadout backfill that already runs on every login. Nothing is destroyed:
   * the catalogue row is still seeded and the `PlayerItem` rows of anybody who
   * bought it survive untouched, so clearing this flag hands the gun straight
   * back to its owners.
   *
   * **It lives here rather than in `catalogue.data.ts`** because the catalogue is
   * the garage's, and a withdrawal has to be honoured by the battle too — the bot
   * roster is dealt in `battle/bots.ts`, which has no account to resolve through
   * and never reads a garage table. This file is the one place both sides already
   * agree on what a turret *is*, so it is where "does this one exist" belongs.
   *
   * A turret is withdrawn when it is not finished, not when it is weak. Balance
   * is a number in the catalogue; this is a statement that the thing should not
   * be in players' hands at all yet.
   */
  withdrawn?: boolean;
}

/**
 * The chassis a hull's track belts are built around, in metres.
 *
 * The belts are **not** part of the hull mesh and cannot be: a belt baked into a
 * fused GLB has no material of its own to scroll, so the hull art is authored
 * with bare wheels and the loop is generated in `tank.ts` (see
 * `docs/track-animation.md`). Generating it means knowing where it goes, and
 * that is a property of the individual hull rather than of the roster — these
 * eighteen chassis are all ~5.6 m long, because image-to-3D normalises the
 * longest axis, but they are **2.49 m (Juggernaut) to 4.11 m wide** and their
 * belly plates sit anywhere from 0.31 m (Warden) to 0.58 m (Marauder) off the
 * ground.
 *
 * One global set of numbers therefore cannot be right for more than one of them,
 * and was not: measured through `frontend/e2e` (`yarn e2e track-fit`), the belts
 * stood up to **0.96 m outboard of the hull's own flank** — two loops of tread
 * running through open air with the tank between them.
 *
 * These live here, beside the mesh path and the yaw, for exactly the reason
 * those do: they are a property of one authored asset, they must travel with it,
 * and fitting a newly authored hull should be one data edit rather than a code
 * path. Every value is derived from the mesh's own measured bounds rather than
 * from an opinion — see `HULL_PROFILES` below.
 */
/**
 * One circle the belt wraps, in the belt's own plane: `z` fore–aft from the
 * loop's centre, `y` up from the ground, `r` the wrapping radius. The wrapping
 * radius is the wheel's radius **plus the belt's own thickness**, because what
 * is being described is where the tread surface runs, not where the steel is.
 */
export interface TrackWheel {
  z: number;
  y: number;
  r: number;
}

export interface RunningGear {
  /** Distance between the drive sprocket and the idler — the belt's flat run. */
  trackLength: number;
  /** Radius the belt wraps at each end. Its top run sits at twice this. */
  wheelRadius: number;
  /**
   * The circles the belt actually wraps — idler, drive sprocket, return
   * rollers — measured off the authored mesh, when somebody has measured them.
   *
   * **Absent means the two-circle loop**: one circle of `wheelRadius` at each
   * end of `trackLength`, which is the shape every belt has always been. The
   * field exists for the hulls that shape lies about — the five chassis with
   * raised drive sprockets (Dart, Harrier, Whippet, Juggernaut, Warden), whose
   * `wheelRadius` is today inflated until the top run clears the sprocket,
   * lifting the whole loop with it. A measured circle list lets the belt climb
   * to the sprocket and come back down, the way the artist drew it.
   *
   * Same discipline as `trackCentreZ`: read off the track-fit rig, never
   * eyeballed. Coordinates are relative to the loop's own centre — the belt is
   * still slid to `trackCentreZ` as a whole.
   */
  wheels?: TrackWheel[];
  /** Width of one belt. */
  trackWidth: number;
  /** Lateral offset of each belt's centreline from the hull's centreline. */
  trackOffsetX: number;
  /**
   * Fore–aft offset of the belt loop's centre from the hull's origin, in
   * metres. Absent means centred, which is where the loop has always been —
   * and where it is wrong on every hull whose authored wheelbase is not
   * centred on its own origin. The Dart's runs 17 cm aft of it, so a centred
   * belt stopped a whole sprocket short of its rear wheel while overshooting
   * the nose. Measured as the midpoint of the lower hull's own footprint
   * (`(lowerZMin + lowerZMax) / 2` off the track-fit rig), never eyeballed.
   */
  trackCentreZ?: number;
  /**
   * Road wheels drawn per side.
   *
   * Only the placeholder body uses them — an authored hull depicts its own — so
   * this is the one field here that never reaches a real tank. It is per hull
   * anyway because it follows from the other four: small wheels mean more of
   * them, and a placeholder that disagreed with the belt it sits inside would be
   * a worse stand-in than one that does not.
   */
  roadWheels: number;
}

/** Worn by the recruit chassis and by anything unrecognised. */
export const DEFAULT_RUNNING_GEAR: RunningGear = {
  trackLength: 4.42,
  wheelRadius: 0.51,
  trackWidth: 0.8,
  trackOffsetX: 1.8,
  roadWheels: 5,
};

export interface HullProfile {
  /** How deep in the progression this chassis is. See `TurretProfile.tier`. */
  tier: number;
  /** Path to the hull mesh. */
  model: string;
  /** Yaw, in radians, that brings the hull's nose round onto +Z. */
  yaw: number;
  /**
   * Height of this hull's deck under the turret ring, in metres above the hull
   * origin — the surface a turret has to sit on.
   *
   * The roster spans 1.518 m (Juggernaut) to 2.980 m (Drover, whose ring is cut
   * into the roof of an armoured tractor cab), which is the whole reason this is
   * data. A single constant for all of them put the same turret through the
   * Marauder's roof and floating over the Juggernaut's — and each art drop has
   * pushed the top of the range further out, the Sledge to 2.491 and then the
   * Drover to 2.980, which is what that constant would have got wrong next.
   *
   * **2.980 is one hair under the `deckHeight < 3` bound in `weapons.test.ts`**,
   * so that bound is now doing real work rather than catching typos. A taller
   * cab is the next thing to fail it, and the failure is the right one: a deck
   * that high wants looking at rather than waving through.
   *
   * It is the **highest** point inside the ring's footprint rather than the
   * mean, because a turret has to clear a raised hatch rather than average it
   * away. Measured by `model-rig.ts`, reported as *Deck Y* by
   * `yarn e2e hull-turret-matrix`; re-measure it when a hull is re-exported.
   */
  deckHeight: number;
  /** Where this chassis wants its belts. See `RunningGear`. */
  gear: RunningGear;
  /**
   * True when this hull runs on **tyres the artist drew**, so no belt is
   * generated around them.
   *
   * The roster's art brief is that a hull arrives with bare road wheels and no
   * belt, because a belt baked into an imported mesh has no material of its own
   * to scroll and smears the whole tank — see `docs/track-animation.md`. Two of
   * the twenty-one deliberately break that: the Drover is a requisitioned farm
   * tractor on big rubber rear tyres, and the Colossus rides on fat solid ones.
   * Generating belts for those two ran a scrolling tread straight through the
   * tyres, which is what this flag stops.
   *
   * **Every measurement passed while it was wrong**, which is the reason this is
   * a flag rather than a judgement made at render time: `track-fit` was 21/21 and
   * the fit matrix 525/525, because the belt was in exactly the right *place* —
   * it simply should not have existed. Only the picture pack could see it.
   *
   * `gear` is still populated for these hulls and must stay so. It is what the
   * **placeholder** body is built from, and a wheeled hull whose GLB fails to
   * load falls back to a generic tracked box that still needs belt dimensions.
   */
  wheeled?: boolean;
}

/**
 * Every turret in the catalogue, by id.
 *
 * Anything missing resolves to `SINGLE_SHOT` on the default model, so a turret
 * added to `catalogue.data.ts` without an entry here is a plain gun rather than
 * a crash.
 */
export const TURRET_PROFILES: Record<TurretId, TurretProfile> = {
  // ── Tier 1 ──
  // The Smart Mesh proving pair's other half — see `hull_recruit`. Its barrel
  // runs along the mesh's +X (read off a top-down render with axis markers), so
  // it takes the -PI/2 that fourteen of the authored turrets take.
  //
  // `DEFAULT_TURRET_MODEL` stays on the old mesh for the same reason the hull's
  // default does: it is the fallback, not this item.
  turret_recruit: {
    tier: 1,
    model: '/assets/turret-recruit.glb',
    yaw: Math.PI,
    mountDrop: 0,
    mountForward: 0.873,
    sizeScale: 1.26,
    muzzleReach: 3.54,
    weapon: SINGLE_SHOT,
  },
  /** Twin barrel: parallel, not fanned, so both rounds land at any range. */
  turret_twinstub: {
    tier: 1,
    model: '/assets/turret-twinstub.glb',
    yaw: 0,
    mountDrop: 0.416,
    mountForward: 0.728,
    sizeScale: 0.962,
    muzzleReach: 2.57,
    weapon: weapon({ archetype: 'multi', shellsPerVolley: 2, volleyOffset: 0.6 }),
  },

  // ── Tier 2 ──
  turret_hornet: {
    tier: 2,
    model: '/assets/turret-hornet.glb',
    yaw: -Math.PI / 2,
    mountDrop: 0.079,
    mountForward: 0.909,
    sizeScale: 1.265,
    muzzleReach: 3.6,
    weapon: SINGLE_SHOT,
  },
  turret_breaker: {
    tier: 2,
    model: '/assets/turret-breaker.glb',
    yaw: 0,
    mountDrop: 0.636,
    mountForward: 0.573,
    sizeScale: 0.786,
    muzzleReach: 1.97,
    weapon: SINGLE_SHOT,
  },
  /**
   * Quad barrel, fanned. The outer pair diverge with distance, so the volley
   * thins itself at long range without any explicit falloff.
   */
  turret_hailstorm: {
    tier: 2,
    model: '/assets/turret-hailstorm.glb',
    yaw: 0,
    mountDrop: 0.953,
    mountForward: 0.673,
    sizeScale: 0.892,
    muzzleReach: 2.33,
    weapon: weapon({
      archetype: 'multi',
      shellsPerVolley: 4,
      volleySpread: 0.05,
      volleyOffset: 0.34,
    }),
  },
  /** Flak: eight pellets in a wide random cone, short range, brutal in a hug. */
  turret_buckshot: {
    tier: 2,
    model: '/assets/turret-buckshot.glb',
    yaw: -Math.PI / 2,
    mountDrop: 0,
    mountForward: 0.071,
    sizeScale: 0.7,
    muzzleReach: 1.41,
    weapon: weapon({
      archetype: 'scatter',
      shellsPerVolley: 8,
      scatterCone: 0.16,
      volleyOffset: 0.22,
    }),
  },
  /** Three rounds a tenth of a second apart, then a long, punishing reload. */
  turret_stutter: {
    tier: 2,
    model: '/assets/turret-stutter.glb',
    yaw: -Math.PI / 2,
    mountDrop: 0,
    mountForward: 0.737,
    sizeScale: 1.085,
    muzzleReach: 2.9,
    weapon: weapon({ archetype: 'burst', shellsPerVolley: 3, burstGapTicks: 4 }),
  },
  /**
   * Mortar: slow, arcing, arms at 24 m, and detonates over an area.
   *
   * **Withdrawn.** The gun that shoots over things wants a sight that shows
   * where the shot will land, and it does not have one — an indirect weapon
   * aimed down a crosshair that cannot express elevation is a gun the player
   * fires hopefully. Everything below is intact and correct; clearing
   * `withdrawn` puts it straight back, including into the hands of anybody who
   * already bought one.
   */
  turret_howler: {
    tier: 3,
    model: '/assets/turret-howler.glb',
    // **-PI/2, not +PI/2.** The tube pointed up and over the *tail* on all 21
    // hulls, and `hull-turret-matrix` passed it every time with a barrel margin of
    // 2.29x: this is a mortar, so the thin geometry it scores is the ready-round
    // rack sitting *forward* of the elevated tube, not the tube. The margin test
    // catches a muzzle too wide to see (Bellows, Hailstorm, Mauler); it does not
    // catch a gun whose rack is thinner than its own barrel, which is the second
    // blind spot in that check and the reason this needed an eye.
    //
    // Withdrawn, so no player has met it — but a withdrawal is a flag that clears,
    // and the yaw would have come back with it.
    yaw: -Math.PI / 2,
    mountDrop: 0.357,
    mountForward: 0.786,
    sizeScale: 0.7,
    muzzleReach: 1.91,
    withdrawn: true,
    weapon: weapon({
      archetype: 'mortar',
      armDistance: 24,
      blastRadius: 13,
      arcHeight: 9,
    }),
  },

  // ── Tier 3 ──
  /** Bounces twice off the arena wall. Bank a shot round a corner, or lead it. */
  turret_carom: {
    tier: 3,
    model: '/assets/turret-carom.glb',
    yaw: Math.PI,
    mountDrop: 0,
    mountForward: 0.844,
    sizeScale: 1.244,
    muzzleReach: 3.46,
    weapon: weapon({ archetype: 'ricochet', bounces: 2 }),
  },
  turret_lancer: {
    tier: 3,
    model: '/assets/turret-lancer.glb',
    yaw: 0,
    mountDrop: 0,
    mountForward: 0.889,
    sizeScale: 1.35,
    muzzleReach: 3.81,
    weapon: SINGLE_SHOT,
  },
  /**
   * Capacitor gun. Hold to charge, release to fire; damage and muzzle velocity
   * both scale from a third to nearly four times the base over 1.2 seconds. A
   * tap is a firecracker, a full charge is the hardest single round in the game
   * short of the rail.
   */
  turret_tempest: {
    tier: 3,
    model: '/assets/turret-tempest.glb',
    // **-45.6 deg, and the roster's only turret yaw that is not a quarter turn.**
    // This mesh's tube is drawn 45.6 deg off its own axes, so the gun aimed well
    // across the hull's nose while the shell still left along the server's
    // `turretYaw` — reported as "tempest looks at a different angle than expected,
    // shooting works fine". `hull-turret-matrix` passed it throughout: that check
    // asks *fore or aft*, and a gun 45 deg off still reaches further forward than
    // back, so it clears the margin comfortably.
    //
    // The figure is the barrel's **axis**, from `barrelBearing` in `model-rig.ts`,
    // not the muzzle's bearing from the pivot. Those differ, and the difference is
    // not academic: a tube mounted off the centreline reads several degrees off
    // while pointing straight, and a trial correction taken from the muzzle's
    // bearing closed only 81% of the error here — consistently on all four turrets
    // it was tried on, which is a conflation rather than noise.
    //
    // Every other turret in the roster measures within a few degrees, and the ones
    // that read larger (Mauler 59, Hoarfrost 27) come with a scatter that says the
    // rig cannot see a tube there — checked by eye and correct. This is the only
    // one a player can see is wrong.
    yaw: -0.7959,
    mountDrop: 0.429,
    mountForward: 0.643,
    sizeScale: 0.7,
    muzzleReach: 1.81,
    weapon: weapon({
      archetype: 'charge',
      chargeTicks: 36,
      minChargeScale: 0.5,
      maxChargeScale: 3.4,
    }),
  },

  // ── Tier 4 ──
  /** Railgun: four and a half seconds of reload, and it goes through people. */
  turret_railspike: {
    tier: 4,
    model: '/assets/turret-railspike.glb',
    yaw: 0,
    mountDrop: 0,
    mountForward: 0.9,
    sizeScale: 1.055,
    muzzleReach: 3.0,
    weapon: weapon({ archetype: 'rail', pierce: 2 }),
  },

  // ── The third art drop: ten more guns ───────────────────────────────────────
  //
  // Design, stats and concept art: `~/Desktop/tank-2d-for-3d/new-tanks/README.md`.
  // Same pipeline as everything above (`docs/model-pipeline.md`) and the same
  // three measured numbers, taken with the same rules:
  //
  //  - **`mountDrop`** is the underside of the turret *body*, walked down from
  //    the widest horizontal slice, measured **unscaled** so `tank.ts` can
  //    multiply it by `sizeScale`. Nine of these ten are a bare body and take 0;
  //    only the Arclight stands on an authored collar.
  //  - **`sizeScale`** fits the mesh into the roster's envelope — 2.30 m across
  //    the ring, 1.80 m of body above the deck — taking whichever binds first.
  //    Eight are bound by height and two (Impaler, Vespid) by the ring, and
  //    fitting *both* is what stops a wide gun being inflated until it out-tops
  //    the hull carrying it.
  //  - **`muzzleReach`** is re-derived per turret because sizing the body sizes
  //    the barrel. It is the mesh's own +Z reach after `sizeScale`.
  //
  // **Every yaw was read off a top-down render with axis markers**, never
  // estimated and never inferred from the numbers, because a half-turn is the
  // error that nothing else here catches. Two things about that are worth
  // writing down, because both cost time:
  //
  //  - The model viewer's rendered X axis is the **negative** of the mesh's own.
  //    Babylon's glTF loader puts a `__root__` node above the scene carrying
  //    `rotationQuaternion (0,1,0,0)` and `scaling (1,1,-1)` — a half turn about
  //    Y and a Z flip, which compose into a mirror in X. `tank.ts` *overwrites*
  //    that node's transform, so the game sees the mesh unmirrored while the
  //    viewer does not. Calibrated against a turret already known to be right:
  //    the Hornet ships `-PI/2` and photographs with its barrel to the left.
  //    So on a top-down render, **barrel to the left takes `-PI/2`, barrel to
  //    the right `+PI/2`, barrel up 0, barrel down `PI`.**
  //  - The rig's thin-geometry test — which side the gun *tube* extends from —
  //    agrees with the renders on nine of these ten and **disagrees on the
  //    Bellows**, whose muzzle is a 2.6 m bell with no tube at all. See its
  //    entry below.

  // ── Tier 1 ──
  /**
   * A long thin barrel on a cheap mount. Out-ranges its tier and hurts nobody
   * quickly, so it is the plain gun with the numbers moved rather than a
   * mechanic.
   *
   * Authored down **-Z** — the only one of the ten that is — hence the half
   * turn. Its cue is the boxed sight raised over the left of the roof, which
   * `turret-01-chaser.png` puts beside a long thin barrel with a two-slot brake.
   */
  turret_chaser: {
    tier: 1,
    model: '/assets/turret-chaser.glb',
    yaw: 0,
    mountDrop: 0,
    mountForward: 0.866,
    sizeScale: 1.097,
    muzzleReach: 3.08,
    weapon: SINGLE_SHOT,
  },

  // ── Tier 2 ──
  /**
   * Five rounds in four tenths of a second, then a long silence.
   *
   * Same committed-burst machinery as the Stutter, two rounds longer and a tick
   * tighter: `burstGapTicks: 3` is 0.1 s between rounds, so the whole burst is
   * 12 ticks. Releasing the trigger cannot cancel it, which is what makes the
   * 2.4 s reload afterwards a real cost rather than something to tap out of.
   *
   * Identified by the six-chambered revolver drum on the right of the mantlet,
   * exactly as `turret-02-ripsaw.png` draws it.
   */
  turret_ripsaw: {
    tier: 2,
    model: '/assets/turret-ripsaw.glb',
    yaw: 0,
    mountDrop: 0.465,
    mountForward: 0.743,
    sizeScale: 1.076,
    muzzleReach: 2.89,
    weapon: weapon({ archetype: 'burst', shellsPerVolley: 5, burstGapTicks: 3 }),
  },
  /**
   * A siege slug at knife range: the hardest single hit below tier 3, and
   * nothing to threaten anyone at distance. The 70 m range is the balance.
   *
   * Its cue is the wide smooth bore under a raised cupola of vision blocks with
   * a camera box beside it — `turret-03-mauler.png`.
   */
  turret_mauler: {
    tier: 2,
    model: '/assets/turret-mauler.glb',
    yaw: 0,
    mountDrop: 0.409,
    mountForward: 0.341,
    sizeScale: 0.734,
    muzzleReach: 1.67,
    weapon: SINGLE_SHOT,
  },

  // ── Tier 3 ──
  /**
   * The round does not stop at the first tank.
   *
   * **`pierce` already existed and needed no engine work at all** — the
   * Railspike has shipped `pierce: 2` since the rail landed, `advanceLeg`
   * already sweeps a circle along the whole leg and collects everything it
   * passes, and the in-flight shell already carries a `struck` id list so a
   * round cannot re-enter a circle it has already hit. This turret is the
   * existing mechanic on a different set of numbers.
   *
   * **It carries no damage falloff, and that is a deliberate deviation from the
   * design doc.** The doc has the shell lose 35% per target pierced; it ships at
   * full damage per target, matching the Railspike, because that is the
   * convention the game already has. Adding falloff would mean a new field on
   * `WeaponProfile` and a new branch in the shared simulation to make one turret
   * an exception to a rule the other piercing gun follows. Do not "fix" this.
   *
   * The ring binds its size rather than its height, and the cue is unmistakable:
   * a stacked-ring collar behind a thick barrel ending in a hexagonal nut, on a
   * hexagonal body with two hatches — `turret-04-impaler.png`.
   */
  turret_impaler: {
    tier: 3,
    model: '/assets/turret-impaler.glb',
    yaw: 0,
    mountDrop: 0,
    mountForward: 0.744,
    sizeScale: 0.874,
    muzzleReach: 2.35,
    weapon: weapon({ archetype: 'rail', pierce: 2 }),
  },
  /**
   * **TODO — this is not the gun it is designed to be.** The Cinder is meant to
   * have **no reload and a heat ceiling**: it fires every 0.28 s for as long as
   * the trigger is held, then locks out for four seconds however hard you hold
   * it, so the decision is when to *stop* shooting rather than when to start.
   *
   * That mechanic is deliberately out of scope here and is a separate decision,
   * because it lands in the one place this codebase is most careful about.
   * Implementing it means a `heat` counter on `TriggerState` and a branch in
   * `stepTrigger` — both in `shared/weapons.ts`, both client-predicted, both run
   * by the server *and* the browser. Heat that diverges by one tick locks the
   * gun out at different moments on the two sides, which is the most visible
   * netcode failure there is. The charge meter survives exactly that discipline,
   * so the pattern to copy exists; it is still a change to the shared
   * simulation's state machine and it wants its own pass and its own tests.
   *
   * What ships instead is the **nearest existing archetype**: a committed burst
   * of eight rounds 0.267 s apart — a 1.87 s stream — behind a 4.5 s reload.
   * That is a real, buyable, working gun with the design's intended *shape*: a
   * long pour of light rounds and then a silence you cannot shorten. It even
   * lands on the design's numbers, ~30 dps while it lasts against the doc's
   * "roughly 25", and 12.4 sustained against its "~12.5". What it does not have
   * is the *choice* — a committed burst runs to completion whether you want it
   * to or not, where the real Cinder would let you stop before the ceiling.
   *
   * The finned barrel and the radiator louvres down both flanks are the cue,
   * and they are the mechanic drawn on the mesh — `turret-05-cinder.png`.
   */
  turret_cinder: {
    tier: 3,
    model: '/assets/turret-cinder.glb',
    yaw: -Math.PI / 2,
    mountDrop: 0.356,
    mountForward: 0.978,
    sizeScale: 1.125,
    muzzleReach: 3.28,
    weapon: weapon({ archetype: 'burst', shellsPerVolley: 8, burstGapTicks: 8 }),
  },
  /**
   * Twin-linked heavies, fired together: both land or neither does.
   *
   * `volleyOffset` and no `volleySpread`, the same bargain as the Twin Stub one
   * tier down — parallel barrels rather than a fan, so the pair stays together
   * at any range and the gun simply looks wide. 0.6 m matches the measured
   * separation of the two tubes on the mesh.
   *
   * Two barrels with heat-blued muzzles under a vision-block cupola, with a
   * stowage basket on the right quarter — `turret-06-warhound.png`.
   */
  turret_warhound: {
    tier: 3,
    model: '/assets/turret-warhound.glb',
    yaw: -Math.PI / 2,
    mountDrop: 0.833,
    mountForward: 0.714,
    sizeScale: 0.84,
    muzzleReach: 2.23,
    weapon: weapon({ archetype: 'multi', shellsPerVolley: 2, volleyOffset: 0.6 }),
  },

  // ── Tier 4 ──
  /**
   * **The chain, as designed.** The hit arcs to the nearest *other* enemy within
   * 12 m for half damage: ordinary against a lone target, punishing against a
   * squad holding ground together, and the direct counter to a formation.
   *
   * It shipped as a plain heavy single shot for one release and could not stay
   * one, because the gun then went on sale for diamonds. Without the arc it is
   * 15.4 dps at 160 m on a 2.5 rad/s ring, which the tier-3 Lancer beats on all
   * three at less than half the valuation — a premium item that is a worse
   * Lancer is not a bounded advantage, it is a bad sale. So the mechanic came
   * first and the price came after.
   *
   * ── Where it lives, and why the numbers are here ───────────────────────────
   * Two fields, `chainRadius` and `chainDamageScale`, exactly like `pierce`,
   * `bounces`, `armDistance` and `blastRadius` before them. The behaviour is one
   * pure function — `chainTarget` in `shared/simulation.ts`, sitting beside
   * `blastTargets` because it answers the same shape of question — and one call
   * in `Arena.resolveHit`, which already holds the roster, the positions and the
   * **friendly-fire-filtered target list**. Reusing that list rather than
   * re-deriving one is the whole safety argument: friendly fire is implemented
   * by leaving team-mates out of the sweep, so an arc that took the same list
   * cannot electrocute them, and one that built its own would have been a second
   * place for that rule to be got wrong.
   *
   * The gun draws its second hit from a second `impact` event, which the wire
   * already carries and the client already handles — `endTracer` on an
   * already-ended tracer is a burst and a hit flash at the given point, which is
   * exactly what an arc looks like.
   *
   * ── Three things it is deliberately not ────────────────────────────────────
   * **Not a `blastRadius`.** `blastTargets` catches *everyone* inside the radius
   * with linear falloff; this catches one tank for a flat half. Splash would
   * have been a different, stronger gun wearing this one's name.
   *
   * **Not a new archetype.** `WeaponArchetype` is the roster's public
   * vocabulary: every value has a fire sound, a synthesised fallback and a place
   * in the loudness ordering (`game/audio/policy.ts`), and it is the unit the
   * rule *every archetype must be buyable for credits* is written in. An eighth
   * value whose only seller is a premium gun would want a new sample under
   * `frontend/public/assets/` and would put a second mechanic behind money on
   * the day that rule was written. The Impaler is the precedent: it is `rail`
   * with `pierce: 2`, and what makes it its own gun is a number.
   *
   * **Not a chain of chains.** One hop. Two prongs, one arc — a round that kept
   * jumping would make a stack of four tanks a wipe, and the damage a shot could
   * do would stop being bounded by anything on the card.
   *
   * The only turret of the ten standing on an authored collar, hence the
   * 0.5 m `mountDrop`. Cue: the copper coil stack, the looping cables into a
   * flank canister, and the pronged emitter in place of a muzzle —
   * `turret-07-arclight.png`.
   */
  turret_arclight: {
    tier: 4,
    model: '/assets/turret-arclight.glb',
    yaw: 0,
    // 0.525 rather than the 0.50 the slice walk reports: `bodyProfile` bins the
    // mesh in 5 cm slabs, so its answer is quantised and lands the seat 0.029 m
    // shy. Trimmed against the rig until `hull-turret-matrix` reads 0.050 m,
    // exactly as the Tempest's 0.30 was trimmed from a measured 0.25.
    mountDrop: 0.692,
    mountForward: 0.629,
    sizeScale: 0.795,
    muzzleReach: 2.04,
    // 12 m is a little under four tank lengths, so the arc reaches a formation
    // and does not reach a line of tanks spread for exactly this reason. Half
    // damage keeps the second hit worth aiming for a pair and never worth more
    // than the first: 40 + 20 against two tanks is 23.1 dps, the top of the band
    // rather than past it.
    weapon: weapon({ chainRadius: 12, chainDamageScale: 0.5 }),
  },
  /**
   * Ten pellets at fifty-five metres — the Buckshot's big brother, and the same
   * bargain: extraordinary inside a hug, irrelevant beyond it.
   *
   * The cone is 0.15 rad against the Buckshot's 0.16. Marginally tighter because
   * the extra seven metres of range would otherwise be worth nothing at all: at
   * 55 m a 0.15 rad half-angle is already 8.3 m of spread, three tanks wide.
   *
   * **Its `yaw` is the one place the rig's own backwards check is wrong, and
   * this is the reason.** `barrelReach` in `model-rig.ts` finds the gun by
   * looking for geometry thin in *both* cross-axes, which is the right test for
   * a tube and no test at all for a 2.6 m flared bell — so it ignores the muzzle
   * entirely and picks whichever incidental brackets are thin, scoring 1.95 m
   * forward against 2.15 m back and calling the gun reversed. The bell is on the
   * mesh's +X and takes `-PI/2`, read off a top-down render with axis markers
   * and confirmed in `tank-battle-snapshots`, where the arena camera sits behind
   * the gun and a correct barrel points away from the viewer.
   *
   * The heuristic now reports a **margin** alongside its verdict and refuses to
   * fail a turret it cannot see the muzzle of — this one and the Hailstorm both
   * land at 1.08x and the Mauler at 1.15x, against 1.4x–3.4x for every gun with
   * a real tube — so the check no longer condemns the bell, and no longer
   * endorses it either.
   *
   * **This value was flipped to `+PI/2` once, to make that false positive go
   * green, and flipping it was wrong.** Settled by photographing the real
   * `TankVisual` from the rig's own front view — eye on +Z, so a correct barrel
   * points at the camera — beside the Sunderer, which the check passes with all
   * of its thin geometry forward and none back. Both show their bore to the
   * camera; the bell is the wider of the two and just as plainly aimed. If the
   * matrix ever reports this turret as reversed again, take the picture before
   * touching the number.
   */
  turret_bellows: {
    tier: 4,
    model: '/assets/turret-bellows.glb',
    yaw: -Math.PI / 2,
    mountDrop: 0,
    mountForward: 0.357,
    sizeScale: 0.7,
    muzzleReach: 1.61,
    weapon: weapon({
      archetype: 'scatter',
      shellsPerVolley: 10,
      scatterCone: 0.15,
      volleyOffset: 0.24,
    }),
  },

  // ── Tier 5 ──
  /**
   * One shell, five seconds, and most of a Bastion. The slowest gun in the game
   * and the hardest single round in it — 110 damage on a 5.2 s reload, which is
   * 21.2 dps and squarely inside the band. Miss and the next five seconds are
   * somebody else's, which is the whole balance.
   *
   * Cue: the long thick barrel with a slotted brake and the recoil rails over a
   * blocky breech — `turret-09-sunderer.png`, the render that had to be
   * re-rolled to stop it coming back as a medium gun.
   */
  turret_sunderer: {
    tier: 5,
    model: '/assets/turret-sunderer.glb',
    yaw: 0,
    mountDrop: 0.781,
    mountForward: 0.901,
    sizeScale: 0.832,
    muzzleReach: 2.36,
    weapon: SINGLE_SHOT,
  },
  /**
   * A fast-cycling autocannon that traverses faster than anything can drive —
   * 4.8 rad/s, the quickest ring in the game, on a 0.55 s cycle. The tier-5
   * answer to a hull that arrives before the shot aimed at it.
   *
   * Still `SINGLE_SHOT`: one shell per pull, and the mechanic is entirely in the
   * numbers. Its ring binds its size rather than its height.
   *
   * Cue: the perforated barrel jacket, the belt of visible brass feeding into a
   * box on the right quarter, and the toothed traverse gear round the base —
   * `turret-10-vespid.png`.
   */
  turret_vespid: {
    tier: 5,
    model: '/assets/turret-vespid.glb',
    yaw: -Math.PI / 2,
    mountDrop: 0,
    mountForward: 0.61,
    sizeScale: 1.065,
    muzzleReach: 2.72,
    weapon: SINGLE_SHOT,
  },

  // ── The three bundle guns ──
  //
  // Sold only inside a tank bundle's diamond price, and otherwise ordinary
  // tier-3 turrets: same envelope, same measurements, same `SINGLE_SHOT`. The
  // pack they belong to is `economy/premium.data.ts`; nothing here knows that.
  /**
   * Ashfall's gun. A burning slug out of a stubby, glowing bore — middling
   * numbers on purpose, because what is being sold is the look.
   *
   * Cue: the glowing orange muzzle over a scorched, rust-streaked body with
   * louvred ember vents round the lower ring — `bundle-ashfall-turret.png`.
   */
  turret_emberthrow: {
    tier: 3,
    model: '/assets/turret-emberthrow.glb',
    yaw: -Math.PI / 2,
    // Zero, and the flat disc under the drum is the reason rather than an
    // exception to it: that disc is the *widest* slice of the mesh, so the
    // walk-down from the widest slice stops at the base and the collar is a
    // mounting flange that belongs on the deck rather than inside it. The
    // Arclight is the opposite case — a narrow stalk, which does have to sink.
    mountDrop: 0.102,
    mountForward: 0.664,
    // Height binds, and by a distance: unscaled the body stands 2.282 m above
    // its base against a 2.494 m ring, so the 1.80 m envelope asks 0.811 where
    // the 2.30 m ring would have allowed 0.922. The tallest of the three.
    sizeScale: 0.979,
    muzzleReach: 2.55,
    weapon: SINGLE_SHOT,
  },
  /**
   * Deepfrost's gun. A long cryo-jacketed barrel: honest numbers, unmistakable
   * silhouette.
   *
   * Cue: the ribbed bellows jacket down the barrel, the loop pipework and the
   * spherical accumulator over the breech — `bundle-deepfrost-turret.png`.
   */
  turret_hoarfrost: {
    tier: 3,
    model: '/assets/turret-hoarfrost.glb',
    yaw: 0,
    mountDrop: 0,
    mountForward: 0.651,
    // Height binds again — 1.85 / 1.631 = 1.134, against 2.30 / 1.897 = 1.212
    // for the ring. Scaled, the ring lands at 2.15 m.
    sizeScale: 0.768,
    muzzleReach: 1.99,
    weapon: SINGLE_SHOT,
  },
  /**
   * Duskrunner's gun. Quick ring, quick reload, unremarkable on paper.
   *
   * Cue: the long thin barrel ending in a bulbous suppressor can, the boxed
   * optic on the roof and the toothed traverse ring at the base — matte black
   * facets with no shine anywhere, `bundle-duskrunner-turret.png`.
   */
  turret_whisper: {
    tier: 3,
    model: '/assets/turret-whisper.glb',
    yaw: 0,
    mountDrop: 0.18,
    mountForward: 0.719,
    // The closest of the three to being bound by both at once: 1.85 / 1.449 =
    // 1.276 on height and 2.30 / 1.788 = 1.286 on the ring, so the scaled ring
    // comes out at 2.28 m — one centimetre inside the envelope.
    sizeScale: 0.834,
    // The longest gun of the three, and the reach is why: 2.001 m unscaled
    // becomes 2.55 m, half a metre past `MUZZLE_OFFSET`.
    muzzleReach: 2.22,
    weapon: SINGLE_SHOT,
  },
};

/**
 * Every hull in the catalogue, by id.
 *
 * ── Where the running-gear numbers came from ─────────────────────────────────
 * Not from looking at the tank. Each hull's GLB was loaded through the real
 * `TankVisual` — real scale, real yaw — and profiled in 5 cm horizontal slabs
 * by `frontend/e2e/rig/model-rig.ts`. Three measurements per hull, and three
 * rules applied to them:
 *
 *  - **`wheelRadius` is the hull's own ground clearance**, the lowest geometry
 *    between the tracks. On a real chassis the belly plate sits at about the
 *    road wheels' axle line, which is the height the belt's ends want to wrap
 *    at — and it is measurable in a fused mesh, where the wheels themselves are
 *    not. It lands within 8 mm of the hand-tuned 0.52 on the hull those
 *    constants were tuned against, which is what says the rule is the right one.
 *  - **`trackOffsetX` and `trackWidth` are proportions of the hull's own flank**
 *    at the belt's height — 0.876 and 0.389 of it, the ratios the tuned recruit
 *    numbers encode. Measured at the belt's height rather than from the bounding
 *    box, because these hulls are widest at the fenders and narrowest at the
 *    belly and the belt lives at neither.
 *  - **`trackLength` is the lower hull's own footprint** less the two end caps
 *    the belt wraps, so the loop encloses the sprocket and the idler instead of
 *    running past them.
 *
 * `yarn e2e track-fit` re-measures all of it and will say so if a re-exported
 * mesh moves. Do not tune these by eye against a screenshot; re-run the case.
 *
 * ── Where the first rule broke, and what replaced it on nine hulls ───────────
 * The belly-clearance rule for `wheelRadius` is right only when the authored
 * end wheels are about belly-height — true of the Bulwark, whose belt reads
 * correct, and false of every chassis drawn with *raised* drive sprockets over
 * a low belly: the Dart, Harrier, Whippet, Juggernaut and Warden derived belts
 * whose top run sliced through their own road wheels and left both sprockets
 * hanging outside the loop. Judged from `tank-snapshots` side views against
 * the Bulwark, hull by hull (the derivation's own case passed throughout —
 * `track-fit` measures the belt against the hull's *flank*, and a belt inside
 * the wheels is inside the flank too). On those hulls `wheelRadius` is instead
 * sized so the top run rides at the sprocket, and `trackLength` re-derived
 * from the same footprint (`span − 2 × wheelRadius`) so the loop's ends stay
 * on the sprockets rather than growing past them.
 *
 * `trackCentreZ` is the second half of the same pass: the belt used to be
 * built centred on the hull origin, and several wheelbases are not — see the
 * field's own note. Both numbers come off the rig's footprint measurements;
 * the eye only decided *which* hulls needed them.
 */
export const HULL_PROFILES: Record<HullId, HullProfile> = {
  // ── Smart Mesh intake, 2026-08-20 ─────────────────────────────────────────
  //
  // The first hull off Tripo's Smart Mesh path, wired here as the deliberate
  // proving pair with `turret_recruit` — the starter loadout, so it is what every
  // new account and the e2e test account drive without buying anything.
  //
  // **It arrives untextured**, with no materials and no UVs: 19,670 triangles of
  // clean quad-derived topology and nothing else. That is a known state, not an
  // oversight — see `frontend/e2e/README.md` under `asset-intake`. The livery
  // still paints it, because the projection is triplanar in object space and
  // needs no UVs; what is missing is the authored albedo the paint is supposed to
  // read panel lines and welds out of.
  //
  // `DEFAULT_HULL_MODEL` is deliberately left pointing at the old mesh: it is the
  // fallback for an unknown id, so repointing it would swap every tank in the
  // game rather than this one.
  hull_recruit: {
    tier: 1,
    model: '/assets/hull-recruit.glb',
    yaw: -Math.PI / 2,
    deckHeight: 1.757,
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.24, roadWheels: 6 },
  },
  hull_scout: {
    tier: 2,
    model: '/assets/hull-scout.glb',
    yaw: -Math.PI / 2,
    deckHeight: 1.667,
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.18, roadWheels: 6 },
  },
  hull_dart: {
    tier: 2,
    model: '/assets/hull-dart.glb',
    yaw: Math.PI,
    deckHeight: 1.348,
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 0.93, roadWheels: 6 },
  },
  hull_bulwark: {
    tier: 2,
    model: '/assets/hull-bulwark.glb',
    // **The only hull in the roster whose mesh is not square to its own axes.**
    // It is drawn 21.5 deg off, which no other hull is (measured: rotating this
    // footprint shrinks its bounding box by 39.3%; every other hull gains 0.0%).
    // Skew, its axis-aligned box read 4.56 m wide against a roster median of 3.27,
    // and `trackOffsetX` — derived for all 21 from the hull's widest half-width —
    // inherited that inflation. So the belts were built to a width the hull does
    // not have, sitting outboard at one end of the run and inboard at the other,
    // which is what a yaw mismatch looks like and is why two attempts at the
    // *gauge* improved it without fixing it. Reported as "its hull model is
    // positioned incorrectly, needs to face the same direction as the tracks".
    yaw: -Math.PI / 2 + 0.3752,
    deckHeight: 1.46,
    // **`trackOffsetX` is 1.44, and this hull is why the figure is not the hull's
    // widest half-width — nor, as a first attempt had it, its median protrusion.** Every hull's was derived that
    // way, which works while a chassis is widest at the *bottom* — and the
    // Bulwark flares the other way: 1.906 m at the ground, widest 2.279 m at
    // y=0.7-0.8, which is the *top* of the belt. Pinned to the maximum, its belts
    // stood 0.354 m outside the lower flank against a roster median of 0.194 and
    // a next-worst of 0.249, so the whole lower half of each belt hung in open
    // air with grass visible between it and the hull. Reported from a playthrough
    // as "bulwark positioning is broken".
    //
    // 1.67 put it on the roster's *median* protrusion (0.354 m -> 0.230 m) and a
    // reviewer still called the belts grossly exposed on all 25 turrets, against
    // four clean hulls beside it — confirmed by eye on the same turret. The median
    // was the wrong target: this hull's lower flank is **undercut**, 1.887 m at the
    // ground against 2.279 m at the belt's top, so a belt sized to the middle of
    // that range stands proud along the bottom however tucked-in it measures
    // against the widest slab. The criterion that holds is the strict one — the
    // belt must be inside the hull at *every* height it occupies — which is
    // 1.887 - 0.43 (half the belt) - 0.02 (skin) = 1.44.
    //
    // `track-fit` measured this hull's belts as *inside* the hull and had done
    // all along, because it compared them against the hull's widest slab — see
    // that case, which now measures the lower flank as well for exactly this.
    // **Both figures are re-derived from the squared-up hull, and both had to be.**
    // Skew, this hull's axis-aligned box read 4.56 x 5.73 m; square it is
    // 3.15 x 5.12 — narrower *and* shorter, because image-to-3D normalises the
    // longest axis and on a skew mesh that axis is the diagonal. So the gauge and
    // the belt length were both sized to a hull that does not exist, and the two
    // earlier attempts at the gauge alone (1.83 -> 1.67 -> 1.44) were chasing a
    // rotation.
    //
    // Set against the roster's own proportions rather than by a fresh rule: the
    // belt protrudes 0.16 m past the narrowest flank, which is what the Recruit
    // and the Dart measure and a reviewer confirmed reads correctly on both; and
    // the belt spans 89.2% of the hull's length, the Recruit's ratio, so it sits
    // ~0.3 m inside each end instead of overhanging them.
    gear: { trackLength: 3.73, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.07, roadWheels: 6 },
  },
  hull_vanguard: {
    tier: 3,
    model: '/assets/hull-vanguard.glb',
    yaw: -Math.PI / 2,
    deckHeight: 2.009,
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.33, roadWheels: 6 },
  },
  hull_marauder: {
    tier: 3,
    model: '/assets/hull-marauder.glb',
    yaw: -Math.PI / 2,
    deckHeight: 1.829,
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.09, roadWheels: 6 },
  },
  hull_juggernaut: {
    tier: 3,
    model: '/assets/hull-juggernaut.glb',
    yaw: -Math.PI / 2,
    deckHeight: 1.583,
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.17, roadWheels: 6 },
  },
  hull_paladin: {
    tier: 4,
    model: '/assets/hull-paladin.glb',
    yaw: -Math.PI / 2,
    deckHeight: 1.813,
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.48, roadWheels: 6 },
  },

  // ── The second art drop ──────────────────────────────────────────────────────
  //
  // Four more chassis, same pipeline and same two measured numbers. Each yaw was
  // read off a top-down render with axis markers, never estimated, and the cue
  // was the doc's: the engine deck, its louvres and its exhausts are at the rear.
  // The convention that turns "which end is the nose" into a number was measured
  // rather than recalled, twice over, because getting the *sign* wrong is a
  // half-turn and a half-turn is the error nothing here catches on its own:
  //
  //  - Against a hull already known to be right — the Dart carries `+π/2` and its
  //    exhaust stacks are on its mesh's **+X**.
  //  - Against the transform itself. `tank.ts` applies the yaw as `rotation.y` on
  //    the authored mesh's root, so the answer is in its world matrix: at
  //    `+π/2` the mesh's local +X comes out along world **-Z**.
  //
  // Both say the same thing: **a nose on -X takes `+π/2`, a nose on +X takes
  // `-π/2`.** Do not re-derive this from memory of Babylon's handedness; read the
  // matrix, or read the Dart.
  //
  // Do not read it off a garage snapshot either. In the hangar's three-quarter
  // view the fore-and-aft axis is nearly along the line of sight, and a hull that
  // is genuinely correct can look reversed there — one was misjudged that way
  // while this was being fitted. `tank-battle-snapshots` is the view that answers
  // it, because the arena camera sits behind the gun and a correct hull therefore
  // shows the camera its **rear**.
  hull_harrier: {
    tier: 2,
    model: '/assets/hull-harrier.glb',
    // The only one of the four authored along Z, so it needs no quarter turn —
    // and the only one where which end is which is a real judgement rather than
    // a grille. Its nose is on +Z: that end carries the driver's periscope dome
    // and the boxed vision block, and the flat rear plate with its single
    // recessed access panel is on -Z, matching `hull-02-harrier.png`. The
    // chassis is deliberately near-symmetric — it is the hull that reverses
    // almost as fast as it advances, with drive sprockets at *both* ends — so
    // there is no sprocket cue here and there was never going to be.
    yaw: -Math.PI / 2,
    deckHeight: 1.874,
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.36, roadWheels: 6 },
  },
  hull_whippet: {
    tier: 3,
    model: '/assets/hull-whippet.glb',
    // Nose on +X. The two louvred engine-deck panels and the pair of exhaust
    // stacks are unmistakably on -X, which is the same cue that reads the Dart.
    yaw: Math.PI,
    deckHeight: 1.499,
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.13, roadWheels: 6 },
  },
  hull_sledge: {
    tier: 3,
    model: '/assets/hull-sledge.glb',
    // Nose on -X: the two slatted grille panels sit on the deck at +X, exactly
    // where `hull-04-sledge.png` puts them, and the vision blocks are forward of
    // the ring on the other end.
    yaw: -Math.PI / 2,
    deckHeight: 1.989,
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.49, roadWheels: 6 },
  },
  hull_warden: {
    tier: 4,
    model: '/assets/hull-warden.glb',
    // The one unambiguous read of the four: a dozer blade is a nose. It is on
    // -X, and the louvred grille is at the far end.
    yaw: Math.PI,
    deckHeight: 1.947,
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.01, roadWheels: 6 },
  },

  // ── The third art drop: the six that finish the design pass ─────────────────
  //
  // Same pipeline, same two measured numbers, same rule for the yaw — read off a
  // top-down render with axis markers, never estimated, with the rear identified
  // by the engine deck's louvres, grilles or exhausts. The convention is the one
  // stated above and it has not moved: **nose on -X takes `+PI/2`, nose on +X
  // takes `-PI/2`, a nose already on +Z takes 0.**
  //
  // Five of these six were authored along X with the nose on -X, so five carry
  // `+PI/2`; only the Drover came out along Z. That is a fact about how the
  // concept art was framed rather than a shortcut — each was read individually,
  // and the Drover is the proof the reading was not a default.
  //
  // The warning above still applies and is worth repeating once per drop: do not
  // judge a hull's facing in `tank-snapshots`. In the garage's three-quarter view
  // the fore-and-aft axis is nearly along the line of sight and a correct hull can
  // look reversed. `tank-battle-snapshots` is the view that answers it, because
  // the arena camera sits square behind the gun and a correct tank shows the
  // camera its rear.
  hull_drover: {
    tier: 1,
    model: '/assets/hull-drover.glb',
    // The only one of the six authored along Z, and the easiest read in the whole
    // roster: this is a farm tractor with plate welded over the cab, so the nose
    // is the radiator. The vertical-slatted radiator and the ribbed bumper grille
    // below it are on +Z, the huge rubber rear tyres on -Z, exactly as
    // `hull-01-drover.png` draws it.
    yaw: -Math.PI / 2,
    // The tallest deck in the roster, and legitimately so: the ring is cut into
    // the roof of an armoured cab that sits over the rear axle, so the plate a
    // turret lands on *is* the highest thing on the vehicle — `deckMaxY` and the
    // hull's own bounding-box top agree to a millimetre at 2.980. The median under
    // the ring is 2.878, only 0.10 below it, so this is a flat roof rather than one
    // spike winning: on the shipped roster the Harrier, Vanguard and Colossus all
    // diverge by 0.85–1.07 and are correct.
    deckHeight: 2.356,
    // A tractor rolls on tyres, and this one's are drawn on the mesh — the huge
    // rubber rears and the small front bogies both. Generating a belt around them
    // ran a scrolling tread through the tyres. The numbers below stay because the
    // placeholder is built from them.
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.18, roadWheels: 6 },
  },
  hull_redoubt: {
    tier: 2,
    model: '/assets/hull-redoubt.glb',
    // Nose on -X: the two louvred engine-deck panels sit on the deck at +X, and
    // the pair of slitted vision blocks that `hull-03-redoubt.png` puts on the
    // front plate are on the -X face. Both cues agree, which is what the doc asks
    // for on a hull this near-symmetric in plan.
    yaw: -Math.PI / 2,
    // Second tallest, for the same structural reason as the Drover's: the whole
    // hull is one truncated pyramid and the ring is cut into its flat top, so the
    // deck is the hull's top. Max 2.629 against a median of 2.597 — 0.03 apart,
    // the flattest deck of the six.
    deckHeight: 1.684,
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.18, roadWheels: 6 },
  },
  hull_bastion: {
    tier: 3,
    model: '/assets/hull-bastion.glb',
    // Nose on -X. This hull has no grille to read, so the cue is the running gear
    // and the glacis: the toothed drive sprocket is at +X and the plain idler at
    // -X, and the sloped, chevron-marked glacis plate is on the -X end — which is
    // where `hull-06-bastion.png` draws both.
    yaw: -Math.PI / 2,
    // 2.397 max against 2.297 median, with the hull itself topping out at 2.465 —
    // so the ring sits on the deck proper and the taller 0.07 is a fitting outside
    // the ring footprint, which is exactly the case `deckUnderRing` exists for.
    deckHeight: 1.583,
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.15, roadWheels: 6 },
  },
  hull_falcon: {
    tier: 4,
    model: '/assets/hull-falcon.glb',
    // Nose on -X, and the cue is as loud as an exhaust gets: two hexagonal nozzles
    // stand out of the rear block on +X, matching the raised vented engine deck in
    // `hull-08-falcon.png`. The faceted, chevron-marked prow is on -X.
    yaw: -Math.PI / 2,
    // 2.010 max against 1.995 median — 0.015 apart, the flattest ring seat in the
    // whole roster, which is what a raised machined collar looks like when it is
    // measured. The hull reaches 2.106 at the rear engine block, outside the ring.
    deckHeight: 1.555,
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.18, roadWheels: 6 },
  },
  hull_colossus: {
    tier: 5,
    model: '/assets/hull-colossus.glb',
    // Nose on -X: the crosshatched radiator grille lies on the deck immediately
    // aft of the ring at +X, and the raised driver's vision block is on the -X
    // face. `hull-09-colossus.png` puts the deck grille at the rear and the
    // orange-framed vision port at the front.
    yaw: -Math.PI / 2,
    // 2.485 max against 1.513 median. That 0.97 gap is the widest of the six and it
    // is *not* a spike: the ring is a genuinely open hole, so most cells inside the
    // footprint sample the hull's floor a metre down. The shipped Harrier (1.07)
    // and Vanguard (1.01) read the same way and are correct. The maximum is the rim
    // the turret has to clear, which is why the profile stores the maximum.
    deckHeight: 1.49,
    // Eight fat solid tyres, drawn on the mesh. Same reason as the Drover, and
    // this is the one the belt looked worst on — the generated tread crossed the
    // tyres rather than sitting inboard of them.
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.18, roadWheels: 6 },
  },
  hull_phantom: {
    tier: 5,
    model: '/assets/hull-phantom.glb',
    // Nose on -X, and this one cannot be misread: three turbine engines sit in a
    // cluster on +X, nozzles outward, exactly as `hull-10-phantom.png` draws them
    // over the rear deck. The exposed tubular space frame runs forward of them to
    // the -X prow.
    yaw: -Math.PI / 2,
    // 1.939 max against 1.809 median, with the hull reaching 2.282 at the turbines
    // — which sit well aft of the ring and so are correctly ignored.
    deckHeight: 1.731,
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.35, roadWheels: 6 },
  },

  // ── The three bundle chassis ──
  //
  // Sold only inside a tank bundle, and otherwise ordinary tier-3 hulls. All
  // three are authored with bare road wheels, so the belt is generated exactly
  // as it is for the rest of the roster — none of them is `wheeled`.
  hull_cinderhulk: {
    tier: 3,
    model: '/assets/hull-cinderhulk.glb',
    // Nose on +X: the two louvred engine-deck panels sit aft of the ring on -X,
    // and the towing shackles and rounded glacis are on +X, which is where
    // `bundle-ashfall-hull.png` draws both.
    yaw: -Math.PI / 2,
    // 2.003 max against a 1.358 median, with the hull itself topping out at
    // 2.026. That 0.64 gap is the open ring again — most cells inside the
    // footprint sample the hull floor a metre down, exactly as the shipped
    // Colossus (0.97) and Harrier (1.07) do. The maximum is the rim the turret
    // has to clear, so the maximum is what the profile stores.
    deckHeight: 1.647,
    // Belly at 0.405, so the belt wraps at 0.40 and its top run sits at 0.80 —
    // under the 1.55 m flank the slabs report over that span, which the 1.26
    // centreline plus a 0.35 half-width clears by 0.06. The narrowest chassis of
    // the three and the longest lower hull: 5.53 m of belt against a 5.53 m
    // footprint, so it runs the length of the tank and past neither end.
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.08, roadWheels: 6 },
  },
  hull_glacier: {
    tier: 3,
    model: '/assets/hull-glacier.glb',
    // Nose on +X. Two ribbed engine-deck grilles and a pair of round hatches lie
    // on -X; the tow hooks, cable runs and the sloped bow plate are on +X, as in
    // `bundle-deepfrost-hull.png`.
    yaw: -Math.PI / 2,
    // 1.810 max and 1.810 at the hull's own top: the flattest, lowest deck of
    // the three, and the ring is a shallow recess in it rather than a hole.
    deckHeight: 1.829,
    // Belly at 0.429. The hull swells from 1.55 to 1.66 across the belt's own
    // height, so the flank it is measured against is the sponson at 0.75 rather
    // than the belly plate, and 1.38 + 0.34 stands 0.06 proud of it.
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.2, roadWheels: 6 },
  },
  hull_nightjar: {
    tier: 3,
    model: '/assets/hull-nightjar.glb',
    // The one of the three drawn down its own +Z, so no correction. The louvred
    // engine deck and the rear plate are on -Z and the faceted, chevron-marked
    // glacis is on +Z — `bundle-duskrunner-hull.png` puts them the same way.
    yaw: -Math.PI / 2,
    // 2.220 max against a 2.009 median and a 2.228 hull top — the ring is cut
    // into the highest plate on the vehicle, so the deck is very nearly the
    // hull, and the tallest deck of the three.
    deckHeight: 1.639,
    // The widest of the three by a distance — 1.88 m of half-width where the
    // belt runs, against the Cinderhulk's 1.55 — so its belts stand furthest
    // out at 1.58, and its lower hull is the shortest, which is what pulls the
    // run in to 4.29.
    gear: { trackLength: 4.3, wheelRadius: 0.42, trackWidth: 0.8, trackOffsetX: 1.23, roadWheels: 6 },
  },
};

/** The weapon behaviour for a turret id, or the plain gun if it has none. */
export function weaponFor(turretId: TurretId | null | undefined): WeaponProfile {
  return (turretId && TURRET_PROFILES[turretId]?.weapon) || SINGLE_SHOT;
}

/**
 * Whether this turret has been taken out of the game. See `TurretProfile.withdrawn`.
 *
 * An unknown id is **not** withdrawn: it resolves to the plain single-shot gun
 * everywhere else in this file, and reporting it as withdrawn would make a typo
 * silently confiscate somebody's turret rather than fall back to a dull one.
 */
export function isWithdrawnTurret(turretId: TurretId | null | undefined): boolean {
  return turretId ? TURRET_PROFILES[turretId]?.withdrawn === true : false;
}

/** Every turret currently withdrawn, for the places that need to filter a list. */
export function withdrawnTurretIds(): string[] {
  return Object.entries(TURRET_PROFILES)
    .filter(([, profile]) => profile.withdrawn === true)
    .map(([id]) => id);
}

/**
 * The tier an unrecognised id is treated as.
 *
 * One, which is to say *always legal*, and it is the same instinct every other
 * total lookup in this file follows: an unknown turret is `SINGLE_SHOT` on the
 * default mesh, so an unknown id already resolves to the plainest thing in the
 * game rather than to the richest. Reporting it as tier 5 would make a typo
 * silently substitute a real tank for a placeholder one, which hides the typo.
 *
 * The guard against a *genuinely* missing entry is not this number — it is
 * `garage/catalogue-parity.test.ts`, which fails when a catalogue hull or turret has
 * no profile here at all.
 */
export const DEFAULT_ITEM_TIER = 1;

/** How deep in the progression a hull is. See `TurretProfile.tier`. */
export function tierForHull(hullId: HullId | null | undefined): number {
  return (hullId && HULL_PROFILES[hullId]?.tier) || DEFAULT_ITEM_TIER;
}

/** How deep in the progression a turret is. See `TurretProfile.tier`. */
export function tierForTurret(turretId: TurretId | null | undefined): number {
  return (turretId && TURRET_PROFILES[turretId]?.tier) || DEFAULT_ITEM_TIER;
}

/** Mesh path for a hull id. One data edit per hull when the art arrives. */
export function modelForHull(hullId: HullId | null | undefined): string {
  return (hullId && HULL_PROFILES[hullId]?.model) || DEFAULT_HULL_MODEL;
}

/** Mesh path for a turret id. One data edit per turret when the art arrives. */
export function modelForTurret(turretId: TurretId | null | undefined): string {
  return (turretId && TURRET_PROFILES[turretId]?.model) || DEFAULT_TURRET_MODEL;
}

/**
 * Yaw correction for a hull's mesh.
 *
 * Paired with `modelForHull` rather than folded into it because the renderer
 * needs both, and an unknown id has to fall back to the default model *and* the
 * default model's own correction — returning 0 here for an id that resolved to
 * the default mesh would leave that tank facing sideways.
 */
export function yawForHull(hullId: HullId | null | undefined): number {
  const profile = hullId ? HULL_PROFILES[hullId] : undefined;
  return profile ? profile.yaw : DEFAULT_HULL_YAW;
}

/**
 * Running gear for a hull's mesh.
 *
 * Paired with `modelForHull` for the same reason `yawForHull` is: an unknown id
 * falls back to the default *mesh*, so it has to fall back to that mesh's own
 * chassis too. Returning a neutral set here would wrap the recruit hull in
 * somebody else's belts.
 */
export function gearForHull(hullId: HullId | null | undefined): RunningGear {
  const profile = hullId ? HULL_PROFILES[hullId] : undefined;
  return profile ? profile.gear : DEFAULT_RUNNING_GEAR;
}

/**
 * Whether this chassis rolls on tyres its artist drew, rather than on bare road
 * wheels waiting for a generated belt.
 *
 * Defaults to false, which is the right default twice over: it is what sixteen of
 * the twenty-one hulls are, and a hull added without thinking about this gets belts
 * — visibly wrong on a wheeled mesh, and therefore noticed — rather than silently
 * losing its running gear, which is the failure nobody reports.
 */
export function isWheeledHull(hullId: HullId | null | undefined): boolean {
  const profile = hullId ? HULL_PROFILES[hullId] : undefined;
  return profile?.wheeled === true;
}

/** Yaw correction for a turret's mesh. See `yawForHull`. */
export function yawForTurret(turretId: TurretId | null | undefined): number {
  const profile = turretId ? TURRET_PROFILES[turretId] : undefined;
  return profile ? profile.yaw : DEFAULT_TURRET_YAW;
}

/**
 * Deck height for a hull that has no profile.
 *
 * The recruit chassis's, because an unknown hull renders `DEFAULT_HULL_MODEL`
 * and that *is* the recruit's mesh — so the fallback pair is a real measured
 * hull rather than an average of eight that matches none of them.
 */
export const DEFAULT_DECK_HEIGHT = 2.123;

/**
 * Mount drop for the fallback turret mesh, which carries a 1.35 m collar.
 *
 * The fallback's own number, not the recruit's. They were the same figure while
 * the recruit pair *was* the fallback mesh; since the Smart Mesh intake the
 * recruit has its own art and these four constants describe `tank-turret.glb`
 * and `tank-hull.glb` alone.
 */
export const DEFAULT_MOUNT_DROP = 1.35;

/**
 * Forward shift for the fallback turret mesh. Zero, and deliberately.
 *
 * `DEFAULT_TURRET_MODEL` is `tank-turret.glb`, the pre-roster box whose origin is
 * already at its ring — and more to the point, this is what a *failed* load falls
 * back to. A correction measured off one mesh must never be applied to a different
 * one, so the fallback states none rather than borrowing the roster's.
 */
export const DEFAULT_MOUNT_FORWARD = 0;

/** Size and reach of the fallback turret mesh, measured off it. */
export const DEFAULT_SIZE_SCALE = 1.125;
export const DEFAULT_MUZZLE_REACH = 2.3;

/**
 * Deck height for a hull id. See `HullProfile.deckHeight`.
 *
 * Pairs with `mountDropForTurret` to seat a turret: the ring goes to the deck,
 * the mesh sinks by its own collar, and the turret body lands on the deck
 * whatever the combination. Neither number can do it alone — the deck varies by
 * hull and the collar by turret, and they are independent.
 */
export function deckHeightForHull(hullId: HullId | null | undefined): number {
  const profile = hullId ? HULL_PROFILES[hullId] : undefined;
  return profile ? profile.deckHeight : DEFAULT_DECK_HEIGHT;
}

/** How far to sink a turret's mesh. See `TurretProfile.mountDrop`. */
export function mountDropForTurret(turretId: TurretId | null | undefined): number {
  const profile = turretId ? TURRET_PROFILES[turretId] : undefined;
  return profile ? profile.mountDrop : DEFAULT_MOUNT_DROP;
}

/** How far forward to push a turret's mesh. See `TurretProfile.mountForward`. */
export function mountForwardForTurret(turretId: TurretId | null | undefined): number {
  const profile = turretId ? TURRET_PROFILES[turretId] : undefined;
  return profile ? profile.mountForward : DEFAULT_MOUNT_FORWARD;
}

/** How large to draw a turret. See `TurretProfile.sizeScale`. */
export function sizeScaleForTurret(turretId: TurretId | null | undefined): number {
  const profile = turretId ? TURRET_PROFILES[turretId] : undefined;
  return profile ? profile.sizeScale : DEFAULT_SIZE_SCALE;
}

/** Where this turret's barrel tip is. See `TurretProfile.muzzleReach`. */
export function muzzleReachForTurret(turretId: TurretId | null | undefined): number {
  const profile = turretId ? TURRET_PROFILES[turretId] : undefined;
  return profile ? profile.muzzleReach : DEFAULT_MUZZLE_REACH;
}

// ─── Deterministic spread ────────────────────────────────────────────────────

/**
 * A stable pseudo-random number in [-1, 1) from three integers.
 *
 * `Math.random()` is forbidden in a shared simulation, and a scattergun needs
 * spread, so the spread is *derived* rather than drawn. `Math.imul` is exact
 * 32-bit multiplication in every engine, so the same three integers give the
 * same number on the server and in the browser, byte for byte.
 */
export function shellNoise(seed: number, shot: number, index: number): number {
  let h = (seed ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (shot >>> 0), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (index >>> 0), 0xc2b2ae35) >>> 0;
  // Every step ends `>>> 0`, including the plain XORs. `^` in JavaScript yields
  // a *signed* 32-bit result, so dropping the coercion after the last fold lets
  // half of all seeds come back negative — and a scatter cone twice as wide on
  // one side as the other, with pellets outside the cone entirely.
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 0x27d4eb2d) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return (h / 0x100000000) * 2 - 1;
}

/**
 * Folds a player id into the 32-bit seed `shellNoise` wants. FNV-1a, chosen
 * because it is four lines and its result does not depend on anything but the
 * string.
 */
export function seedFromPlayerId(playerId: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < playerId.length; i += 1) {
    h ^= playerId.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

// ─── The trigger ─────────────────────────────────────────────────────────────

/** One shell of a volley, described relative to the turret's own bearing. */
export interface VolleyShot {
  /** Radians off the turret's bearing. Fan plus derived scatter. */
  angleOffset: number;
  /** Metres sideways from the muzzle, perpendicular to the bearing. */
  lateralOffset: number;
  /** Multiplies `TankSpec.damage`. Only a charge weapon moves this. */
  damageScale: number;
  /** Multiplies `TankSpec.muzzleVelocity`. Only a charge weapon moves this. */
  speedScale: number;
}

/**
 * Everything the trigger remembers between ticks.
 *
 * Held per player on the server and mirrored on the client. `seed` is fixed at
 * creation from the player id, so a scattergun's cone is reproducible from the
 * shot ordinal alone.
 */
export interface TriggerState {
  /** Ticks until the gun is loaded. Zero when ready. */
  reloadTicks: number;
  /** Ticks the trigger has been held with a charge weapon. */
  chargeTicks: number;
  /** Rounds still owed by a committed burst. */
  burstRemaining: number;
  /** Ticks until the next round of a burst. */
  burstDelay: number;
  /** Volleys fired, ever. Seeds the scatter cone — never reset mid-life. */
  shotIndex: number;
  /** Fixed per player. */
  seed: number;
}

export function initialTrigger(seed = 0): TriggerState {
  return {
    reloadTicks: 0,
    chargeTicks: 0,
    burstRemaining: 0,
    burstDelay: 0,
    shotIndex: 0,
    seed: seed >>> 0,
  };
}

export interface TriggerResult {
  trigger: TriggerState;
  /** Shells to spawn on this tick, in barrel order. Empty on most ticks. */
  volley: VolleyShot[];
}

/**
 * Reload as a whole number of ticks, rounded up so it is never shorter than the
 * spec promises. Lives here rather than in `simulation.ts` because the trigger
 * is the only thing that consumes it.
 */
export function reloadTicksFor(reloadSeconds: number): number {
  if (!Number.isFinite(reloadSeconds)) return 1;
  return Math.max(1, Math.ceil(reloadSeconds / TICK_DT));
}

/**
 * Advances the trigger by exactly one tick.
 *
 * Called **once per simulation tick per player**, never once per input command.
 * That distinction matters: the arena may apply up to three queued inputs on a
 * catch-up tick, and a charge that counted inputs rather than ticks would fill
 * three times as fast for a client that had hiccuped — a divergence the browser
 * could not reproduce. `fire` is therefore the flag from the *last* input
 * applied this tick.
 *
 * `fire` is a flag, not a request: it says *when*, never *what* or *where*. How
 * many shells come out, in which directions, and with how much energy behind
 * them is decided entirely here, from the profile and from how long the trigger
 * has been down.
 */
export function stepTrigger(
  state: TriggerState,
  fire: 0 | 1,
  profile: WeaponProfile,
  reloadSeconds: number,
): TriggerResult {
  return advance(state, fire ? 'down' : 'up', profile, reloadSeconds);
}

/**
 * Advances the gun on a tick where the player's intent is simply unknown —
 * the server received no input for them at all.
 *
 * The reload keeps running down and a committed burst keeps firing, because
 * neither is the player's business once started. What is *frozen* is the
 * charge: the trigger has not been released, it has gone quiet, and treating
 * silence as a release would loose a round nobody asked for.
 *
 * That distinction is not a nicety. Node's own timers jitter by a millisecond
 * or two, so a client sending at a true 30 Hz still starves the odd tick, and
 * an earlier version that read every starved tick as a release turned an
 * eighteen-tick charge into a one-tick one — the meter had been reset on tick
 * seventeen and nobody could see why. A charge should be lost to a *stall*,
 * which the caller decides by how long the silence has gone on, not to a tick.
 */
export function holdTrigger(
  state: TriggerState,
  profile: WeaponProfile,
  reloadSeconds: number,
): TriggerResult {
  return advance(state, 'quiet', profile, reloadSeconds);
}

/** 'down' the trigger is held, 'up' it is released, 'quiet' nothing was heard. */
type TriggerMode = 'down' | 'up' | 'quiet';

function advance(
  state: TriggerState,
  mode: TriggerMode,
  profile: WeaponProfile,
  reloadSeconds: number,
): TriggerResult {
  const next: TriggerState = { ...state };
  if (next.reloadTicks > 0) next.reloadTicks -= 1;

  // A burst, once started, owns the gun until it is finished. Nothing the
  // player does with the trigger in the meantime is heard.
  if (next.burstRemaining > 0) {
    next.burstDelay -= 1;
    if (next.burstDelay > 0) return { trigger: next, volley: [] };
    next.burstRemaining -= 1;
    next.burstDelay = Math.max(1, profile.burstGapTicks);
    return { trigger: next, volley: fireVolley(next, profile, 1, 1) };
  }

  // Silence is not a trigger release, and it is not a trigger pull either.
  if (mode === 'quiet') return { trigger: next, volley: [] };

  if (profile.chargeTicks > 0) return stepCharge(next, mode === 'down' ? 1 : 0, profile, reloadSeconds);

  if (mode !== 'down' || next.reloadTicks > 0) return { trigger: next, volley: [] };

  next.reloadTicks = reloadTicksFor(reloadSeconds);

  // A burst spends its reload up front and then trickles the rounds out, so the
  // three-round pattern is visible in the fire rate rather than only in the log.
  if (profile.burstGapTicks > 0 && profile.shellsPerVolley > 1) {
    next.burstRemaining = profile.shellsPerVolley - 1;
    next.burstDelay = Math.max(1, profile.burstGapTicks);
    return { trigger: next, volley: fireVolley(next, profile, 1, 1) };
  }

  return { trigger: next, volley: fireVolley(next, profile, profile.shellsPerVolley, 1) };
}

/**
 * Cancels a charge without discharging it.
 *
 * Used when the server stops hearing from a client. Dead reckoning forces the
 * fire flag to zero, and for an ordinary gun that is the end of it — but for a
 * charge weapon "the flag went to zero" *is* the trigger release, so a stalled
 * connection would otherwise loose a fully charged round on the player's behalf.
 * A stalled client must never keep shooting, so the charge is simply lost.
 *
 * A committed burst is deliberately *not* touched. It is not the client
 * shooting — it is the server's own state machine running down something that
 * has already been paid for with a full reload, it is finite, and a stalled
 * client cannot extend it. Cancelling it on a stall was measured against a real
 * connection and cost roughly one burst in three: ordinary network jitter
 * starves a tick now and then, and every starved tick threw away the two rounds
 * the player had already spent the reload on.
 */
export function abortCharge(state: TriggerState): TriggerState {
  if (state.chargeTicks === 0) return state;
  return { ...state, chargeTicks: 0 };
}

/**
 * Empties the gun of everything it was in the middle of.
 *
 * For the cases where the *weapon itself* is no longer the one that was fired:
 * a destroyed tank, and a loadout swapped mid-session. Carrying a committed
 * burst across either would fire the old turret's rounds out of the new one.
 */
export function disarmTrigger(state: TriggerState): TriggerState {
  return { ...state, chargeTicks: 0, burstRemaining: 0, burstDelay: 0 };
}

/** 0–1, for a charge meter. Always 0 on a gun that does not charge. */
export function chargeFraction(state: TriggerState, profile: WeaponProfile): number {
  if (profile.chargeTicks <= 0) return 0;
  return Math.min(1, state.chargeTicks / profile.chargeTicks);
}

function stepCharge(
  next: TriggerState,
  fire: 0 | 1,
  profile: WeaponProfile,
  reloadSeconds: number,
): TriggerResult {
  if (fire) {
    // The charge only builds on a loaded gun, so the meter and the reload
    // gauge never disagree about whether the trigger is doing anything.
    if (next.reloadTicks === 0) {
      next.chargeTicks = Math.min(profile.chargeTicks, next.chargeTicks + 1);
    }
    return { trigger: next, volley: [] };
  }

  const held = next.chargeTicks;
  next.chargeTicks = 0;
  if (held <= 0 || next.reloadTicks > 0) return { trigger: next, volley: [] };

  const t = Math.min(1, held / profile.chargeTicks);
  const scale = profile.minChargeScale + (profile.maxChargeScale - profile.minChargeScale) * t;
  next.reloadTicks = reloadTicksFor(reloadSeconds);
  return { trigger: next, volley: fireVolley(next, profile, profile.shellsPerVolley, scale) };
}

/**
 * Lays out one volley and bumps the shot ordinal.
 *
 * The ordinal — not the tick — is what seeds the scatter. A client cannot know
 * the server's tick number at the moment it pulls the trigger, but it knows
 * exactly how many volleys it has fired, so seeding on the ordinal is what makes
 * the predicted cone identical to the authoritative one rather than merely
 * similar. The `fired` event still carries the server's own directions, so the
 * server remains the authority either way; this only decides whether the
 * prediction is right.
 */
function fireVolley(
  state: TriggerState,
  profile: WeaponProfile,
  count: number,
  scale: number,
): VolleyShot[] {
  const shots = Math.max(1, Math.floor(count));
  const shot = state.shotIndex;
  state.shotIndex = shot + 1;

  const volley: VolleyShot[] = [];
  for (let i = 0; i < shots; i += 1) {
    // Symmetric about the bearing: with two barrels the offsets are ±½ step,
    // with three the middle one is dead on.
    const rank = i - (shots - 1) / 2;
    const scatter =
      profile.scatterCone > 0 ? profile.scatterCone * shellNoise(state.seed, shot, i) : 0;
    volley.push({
      angleOffset: rank * profile.volleySpread + scatter,
      lateralOffset: rank * profile.volleyOffset,
      damageScale: scale,
      speedScale: scale,
    });
  }
  return volley;
}

// ─── Readouts ────────────────────────────────────────────────────────────────

/**
 * Damage per second with everything landing, for the garage's comparison bars.
 *
 * `damage / reloadSeconds` was right when every gun fired one shell; a quad
 * barrel it under-reports by a factor of four. A charge weapon is quoted at
 * full charge including its wind-up, because that is how it is actually played.
 */
export function sustainedDps(
  spec: Pick<TankSpec, 'damage' | 'reloadSeconds'>,
  profile: WeaponProfile = SINGLE_SHOT,
): number {
  const perVolley = spec.damage * profile.shellsPerVolley * volleyScale(profile);
  const cycle = Math.max(TICK_DT, spec.reloadSeconds + windupSeconds(profile));
  return Math.round((perVolley / cycle) * 10) / 10;
}

/** Damage multiplier a volley is quoted at: full charge, or nothing. */
function volleyScale(profile: WeaponProfile): number {
  return profile.chargeTicks > 0 ? profile.maxChargeScale : 1;
}

/** Seconds of wind-up a full-charge shot costs on top of its reload. */
function windupSeconds(profile: WeaponProfile): number {
  return profile.chargeTicks > 0 ? profile.chargeTicks / TICK_RATE : 0;
}

/**
 * A short human label for the archetype, for the garage's turret cards.
 *
 * Here rather than in the frontend so the roster and its vocabulary stay in one
 * file — adding an archetype without naming it is then a type error.
 */
export const ARCHETYPE_LABELS: Record<WeaponArchetype, string> = {
  single: 'Single shot',
  multi: 'Multi-barrel',
  burst: 'Burst fire',
  charge: 'Charge-up',
  scatter: 'Scattergun',
  mortar: 'Mortar',
  ricochet: 'Ricochet',
  rail: 'Railgun',
};
